import {
  HermesError, lastReply, listDirectory, recentAutomationRuns, replySummary, scanAssets, searchSessions, sessionMessagesRaw,
  type AssetItem, type AutomationRun, type HermesAPI,
} from "@timibot/core";
import { Observable } from "./observable";

function readJSON<T>(key: string): T | undefined {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) as T : undefined; } catch { return undefined; }
}
function writeJSON(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* 存满了就算了 */ }
}

interface CachedSession { lastActive?: number; assets: AssetItem[] }

/**
 * 作品区：所有对话里产出的图片、视频、文件（对应 iOS 的 AssetStore）。
 * 用 Hermes 的全文搜索找出含 `MEDIA:` 的会话，只拉这些会话的消息来解析；结果按会话缓存在本机，会话没变就不再重拉。
 * 作品属于某台主机上的某个 Bot：缓存按“连接 + Bot”分开放。服务器上已经删掉的文件不显示；定时任务的产出也不放这里。
 */
export class AssetStore extends Observable {
  assets: AssetItem[] = [];
  loaded = false;
  error?: string;
  private scanning = new Set<string>();
  private scopes: Record<string, Record<string, CachedSession>> = readJSON("timibot.assets.v2") ?? {};
  private missing: Record<string, string[]> = readJSON("timibot.assetsMissing.v2") ?? {};
  private failed = new Set<string>();
  private scope = "";

  get isScanning(): boolean { return this.scanning.has(this.scope); }

  use(scope: string): void {
    if (scope === this.scope) return;
    this.scope = scope;
    this.loaded = this.scopes[scope] !== undefined;
    this.error = undefined;
    this.rebuild();
  }

  async refresh(api: HermesAPI | undefined, profile: string | undefined): Promise<void> {
    const target = this.scope;
    if (!api || this.scanning.has(target)) return;
    this.scanning.add(target);
    this.changed();
    try {
      const hits = (await searchSessions(api, "MEDIA", 80, profile))
        .filter((h) => h.source !== "cron" && !h.session_id.startsWith("cron_"));
      const known = this.scopes[target] ?? {};
      const stale = hits.filter((h) => { const c = known[h.session_id]; return !c || h.last_active === undefined || c.lastActive !== h.last_active; });
      for (let i = 0; i < stale.length; i += 4) {
        const batch = stale.slice(i, i + 4);
        const results = await Promise.all(batch.map(async (hit) => {
          try { return [hit, scanAssets(await sessionMessagesRaw(api, hit.session_id, profile), hit.session_id, hit.title, hit.source)] as const; }
          catch { return [hit, undefined] as const; }
        }));
        const bucket = (this.scopes[target] ??= {});
        for (const [hit, found] of results) if (found) bucket[hit.session_id] = { lastActive: hit.last_active, assets: found };
        if (this.scope === target) this.rebuild();
      }
      this.scopes[target] ??= {};
      await this.verifyFiles(api, profile, target);
      this.error = undefined;
      writeJSON("timibot.assets.v2", this.scopes);
    } catch (e) {
      if (!this.assets.length && this.scope === target) this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.scanning.delete(target);
      if (this.scope === target) this.loaded = true;
      this.changed();
    }
  }

  /** 同一个会话里的其它作品（“版本”） */
  siblings(item: AssetItem): AssetItem[] {
    if (!item.sessionID) return [];
    return (this.scopes[this.scope]?.[item.sessionID]?.assets ?? []).filter((a) => a.kind === item.kind);
  }

  /** 缩略图加载失败：打不开的作品不显示。服务器明确说找不到就记住，其它失败只在这次打开期间隐藏 */
  markUnavailable(source: string, permanent: boolean): void {
    if (permanent) {
      const list = (this.missing[this.scope] ??= []);
      if (list.includes(source)) return;
      list.push(source);
      writeJSON("timibot.assetsMissing.v2", this.missing);
    } else {
      if (this.failed.has(source)) return;
      this.failed.add(source);
    }
    this.rebuild();
  }

  private rebuild(): void {
    const gone = new Set([...(this.missing[this.scope] ?? []), ...this.failed]);
    const seen = new Set<string>();
    this.assets = Object.values(this.scopes[this.scope] ?? {}).flatMap((c) => c.assets)
      .sort((a, b) => b.timestamp - a.timestamp)
      .filter((a) => {
        if (gone.has(a.source) || seen.has(a.source)) return false;
        // 定时任务产出的不放进作品；回复里引用的、看不出是什么文件的网址也不是作品
        if (a.origin === "cron" || a.sessionID.startsWith("cron_")) return false;
        if (/^https?:\/\//.test(a.source) && a.kind === "file") return false;
        seen.add(a.source);
        return true;
      });
    this.changed();
  }

  /** 按目录把服务器上的文件名列一遍，找出已经删掉的作品。列不出来的目录不算删除。 */
  private async verifyFiles(api: HermesAPI, profile: string | undefined, target: string): Promise<void> {
    const paths = new Set(Object.values(this.scopes[target] ?? {}).flatMap((c) => c.assets).map((a) => a.source).filter((p) => p.startsWith("/") || p.startsWith("~/")));
    const byDir = new Map<string, string[]>();
    for (const p of paths) {
      const dir = p.slice(0, p.lastIndexOf("/")) || "/";
      byDir.set(dir, [...(byDir.get(dir) ?? []), p]);
    }
    const gone: string[] = [];
    const dirs = [...byDir.keys()].sort().slice(0, 300);
    for (let i = 0; i < dirs.length; i += 6) {
      await Promise.all(dirs.slice(i, i + 6).map(async (dir) => {
        let names: Set<string>;
        try { names = await listDirectory(api, dir, profile); } catch (e) {
          if (e instanceof HermesError && e.kind === "http" && e.status === 404) return; // 老版本 Hermes 没有这个接口
          return;
        }
        for (const p of byDir.get(dir) ?? []) if (!names.has(p.slice(p.lastIndexOf("/") + 1))) gone.push(p);
      }));
    }
    this.missing[target] = gone;
    writeJSON("timibot.assetsMissing.v2", this.missing);
    if (this.scope === target) this.rebuild();
  }
}

/**
 * 工作台“长期目标”：定时任务最近每次运行做了什么（对应 iOS 的 AutomationFeed）。
 * 每条的结果预览取那次运行最后一条助手回复；跑完的运行不会再变，预览缓存在本机，只拉一次。
 */
export class AutomationFeed extends Observable {
  runs: AutomationRun[] = [];
  loaded = false;
  error?: string;
  previews: Record<string, string> = {};
  private scope = "";
  private stored: Record<string, string> = readJSON("timibot.automationPreviews.v1") ?? {};

  async refresh(api: HermesAPI | undefined, profile: string | undefined, scope: string, limit = 20): Promise<void> {
    if (scope !== this.scope) {
      this.scope = scope;
      this.runs = [];
      this.loaded = false;
      this.error = undefined;
      this.previews = {};
      this.changed();
    }
    if (!api) return;
    const target = this.scope;
    try {
      const fresh = await recentAutomationRuns(api, profile, limit);
      if (this.scope !== target) return;
      this.runs = fresh;
      this.error = undefined;
    } catch (e) {
      if (this.scope === target && !this.runs.length) this.error = e instanceof Error ? e.message : String(e);
    }
    this.loaded = true;
    for (const run of this.runs) { const t = this.stored[`${target}|${run.id}`]; if (t) this.previews[run.id] = t; }
    this.changed();
    await this.loadPreviews(api, profile, target, 8);
  }

  /** 前几条没有预览的去拉最后一条回复（正在跑的每次都重新拉） */
  async loadPreviews(api: HermesAPI, profile: string | undefined, target: string, count: number): Promise<void> {
    const wanted = this.runs.slice(0, count).filter((r) => this.previews[r.id] === undefined || r.is_active);
    await Promise.all(wanted.map(async (run) => {
      try {
        const reply = await lastReply(api, run.id, profile);
        if (this.scope !== target || reply === undefined) return;
        const text = replySummary(reply);
        this.previews = { ...this.previews, [run.id]: text };
        if (!run.is_active) this.stored[`${target}|${run.id}`] = text;
        this.changed();
      } catch { /* 预览拉不到就空着 */ }
    }));
    const keys = Object.keys(this.stored);
    if (keys.length > 300) for (const k of keys.slice(0, keys.length - 300)) delete this.stored[k];
    writeJSON("timibot.automationPreviews.v1", this.stored);
  }
}
